import { useEffect, useRef, useState } from "react";
import type { SessionUser } from "@shared/schemas/auth";
import { useAuth } from "@client/hooks/useAuth";

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** `PRODUCT_OWNER` is not a thing to show a person. */
function roleLabel(role: SessionUser["role"]): string {
  switch (role) {
    case "ADMIN":
      return "Administrator";
    case "PRODUCT_OWNER":
      return "Product owner";
    default:
      return "Viewer";
  }
}

/**
 * Who you are and the way out.
 *
 * The identity is on the button rather than behind it — "am I signed in as the
 * right person" is a question people ask constantly on an internal tool and it
 * should not cost a click. Only the actions are in the panel.
 */
export function UserMenu() {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent | TouchEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    // The first action, so the menu is usable without reaching for the mouse.
    panelRef.current?.querySelector("button")?.focus();

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!auth.user) return null;
  const user = auth.user;

  async function onSignOut() {
    setSigningOut(true);
    try {
      await auth.signOut();
    } finally {
      setSigningOut(false);
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-lg border border-line bg-chip-bg py-1 pl-1 pr-2.5 text-left hover:bg-link-bg-hover"
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-md bg-azure-deep text-[11px] font-bold text-on-accent"
        >
          {initials(user.fullName)}
        </span>
        <span className="hidden min-w-0 flex-col leading-tight sm:flex">
          <span className="truncate text-xs font-semibold text-ink">{user.fullName}</span>
          <span className="truncate text-[10px] text-ink-soft">{roleLabel(user.role)}</span>
        </span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Account"
          className="island-shell absolute right-0 top-full z-40 mt-2 flex w-64 flex-col gap-3 p-3"
        >
          <div className="flex flex-col gap-0.5">
            <span className="truncate text-xs font-semibold text-ink">{user.fullName}</span>
            <span className="truncate text-[11px] text-ink-soft">{user.email}</span>
            <span className="text-[11px] text-ink-soft">{roleLabel(user.role)}</span>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => void onSignOut()}
            disabled={signingOut}
            className="rounded-md border border-line px-3 py-2 text-left text-xs font-semibold text-ink-soft hover:bg-link-bg-hover hover:text-ink disabled:opacity-60"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}
